import { useState, useEffect } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", icon: "", description: "", exam_type: "", color: "#0284C7" });

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await api.get("/categories");
      if (Array.isArray(data) && typeof data[0] === "object") {
        setCategories(data);
      }
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/categories/${editing}`, form);
      } else {
        await api.post("/categories", form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", slug: "", icon: "", description: "", exam_type: "", color: "#0284C7" });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (cat) => {
    setForm({ name: cat.name, slug: cat.slug || "", icon: cat.icon || "", description: cat.description || "", exam_type: cat.exam_type || "", color: cat.color || "#0284C7" });
    setEditing(cat.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this category?")) {
      await api.delete(`/categories/${id}`);
      fetchData();
    }
  };

  const inputClass = "w-full admin-input border-b-2 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-categories-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Categories & Exams
            </h1>
            <p className="text-sm admin-text-secondary mt-0.5">{categories.length} categories</p>
          </div>
          <button
            data-testid="add-category-btn"
            onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: "", slug: "", icon: "", description: "", exam_type: "", color: "#0284C7" }); }}
            className="bg-[#0284C7] text-white font-bold py-2.5 px-4 text-xs flex items-center gap-1.5 transition-none"
          >
            <Plus size={14} /> Add Category
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="admin-card border p-5 mb-6 space-y-3" data-testid="category-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Name</label>
                <input data-testid="cat-name-input" placeholder="e.g. Primary TET" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Slug</label>
                <input placeholder="e.g. primary-tet" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Exam Type</label>
                <select value={form.exam_type} onChange={(e) => setForm({ ...form, exam_type: e.target.value })} className={inputClass}>
                  <option value="">Select Type</option>
                  <option value="Teaching">Teaching</option>
                  <option value="Government">Government</option>
                  <option value="Banking">Banking</option>
                  <option value="General">General</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Color</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-10 h-10 cursor-pointer border-none" />
                  <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className={inputClass} />
                </div>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Description</label>
              <textarea placeholder="Brief description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputClass} h-16`} />
            </div>
            <div className="flex gap-2">
              <button data-testid="cat-submit-btn" type="submit" className="bg-[#0284C7] text-white font-bold py-2.5 px-6 text-xs transition-none">
                {editing ? "Update" : "Create"} Category
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="admin-card border font-bold py-2.5 px-6 text-xs transition-none admin-text">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="categories-grid">
          {categories.map((cat) => (
            <div key={cat.id} className="admin-card border p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center text-white font-bold text-sm" style={{ background: cat.color || "#0284C7" }}>
                    {cat.name?.charAt(0) || "?"}
                  </div>
                  <div>
                    <h3 className="text-sm font-bold admin-text">{cat.name}</h3>
                    <p className="text-[10px] admin-text-secondary">{cat.exam_type || "General"}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(cat)} className="p-1.5 admin-text-secondary hover:text-[#0284C7] transition-none">
                    <Edit size={14} />
                  </button>
                  <button onClick={() => handleDelete(cat.id)} className="p-1.5 text-red-400 hover:text-red-500 transition-none">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <p className="text-xs admin-text-secondary leading-relaxed">{cat.description || "No description"}</p>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-[10px] font-bold admin-text-secondary">{cat.course_count || 0} courses</span>
                <span className="text-[10px] font-mono admin-text-secondary">{cat.slug}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
